import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftRight, Building2, Plus, Settings2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AURA_API,
  DEFAULT_AURA_COMPANY_CODE,
  createAuraProject,
  listAuraCompanies,
  listAuraProjects,
  statusLabel,
} from '@/lib/auraApi';
import { usePermissions } from '@/contexts/PermissionsContext';

function statusBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'in_progress') {
    return 'border-amber-500/40 bg-amber-500/15 text-amber-300';
  }
  if (s === 'discovery') {
    return 'border-sky-500/40 bg-sky-500/15 text-sky-300';
  }
  if (s === 'draft') {
    return 'border-[var(--line)] bg-[var(--inset)] text-[var(--text-muted)]';
  }
  return 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300';
}

function pickDefaultCompanyId(rows) {
  if (!Array.isArray(rows) || !rows.length) return '';
  const rts = rows.find(
    (c) => (c.code || '').toUpperCase() === DEFAULT_AURA_COMPANY_CODE,
  );
  return (rts || rows[0]).id;
}

export default function Aura() {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canUpdate = hasPermission('transformations', 'update');
  const [searchParams, setSearchParams] = useSearchParams();
  const companyIdParam = searchParams.get('company') || '';

  const [companies, setCompanies] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [error, setError] = useState('');

  const [createProjectOpen, setCreateProjectOpen] = useState(false);
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);

  const selectedCompany = useMemo(
    () => companies.find((c) => c.id === companyIdParam) || null,
    [companies, companyIdParam],
  );

  const setCompanyId = useCallback(
    (id) => {
      if (id) {
        setSearchParams({ company: id }, { replace: true });
      } else {
        setSearchParams({}, { replace: true });
      }
    },
    [setSearchParams],
  );

  const loadCompanies = useCallback(async () => {
    if (!AURA_API) {
      setError('Transformations API is not configured. Set REACT_APP_BACKEND_URL.');
      setLoadingCompanies(false);
      return;
    }
    setLoadingCompanies(true);
    setError('');
    try {
      const rows = await listAuraCompanies(100);
      setCompanies(rows);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to load companies';
      setError(String(detail));
      setCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  const loadProjects = useCallback(async (cid) => {
    if (!cid) {
      setProjects([]);
      return;
    }
    setLoadingProjects(true);
    setError('');
    try {
      const rows = await listAuraProjects(100, { companyId: cid });
      setProjects(rows);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to load projects';
      setError(String(detail));
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Default company selection after load (URL wins; otherwise RTS / first).
  useEffect(() => {
    if (loadingCompanies || !companies.length) return;
    const valid = companies.some((c) => c.id === companyIdParam);
    if (!companyIdParam || !valid) {
      setCompanyId(pickDefaultCompanyId(companies));
    }
  }, [loadingCompanies, companies, companyIdParam, setCompanyId]);

  useEffect(() => {
    if (companyIdParam && companies.some((c) => c.id === companyIdParam)) {
      loadProjects(companyIdParam);
    } else {
      setProjects([]);
    }
  }, [companyIdParam, companies, loadProjects]);

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!canUpdate) return;
    const trimmed = projectName.trim();
    if (!trimmed || !selectedCompany?.id) return;
    setCreatingProject(true);
    setError('');
    try {
      const project = await createAuraProject({
        name: trimmed,
        description: projectDescription.trim(),
        company_id: selectedCompany.id,
      });
      setCreateProjectOpen(false);
      setProjectName('');
      setProjectDescription('');
      navigate(`/transformations/projects/${project.id}`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Failed to create project';
      setError(String(detail));
    } finally {
      setCreatingProject(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="transformations-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Transformations
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-foreground">
            Dashboard
          </h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            View transformation projects by company. Open a project to run discovery and
            readiness analysis.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild data-testid="aura-open-studio">
            <Link to="/transformations/studio">
              <Settings2 className="mr-2 h-4 w-4" />
              Agent models
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/transformations/companies">Manage companies</Link>
          </Button>
          {canUpdate ? (
            <Button
              type="button"
              onClick={() => setCreateProjectOpen(true)}
              disabled={!selectedCompany}
              data-testid="aura-new-project"
            >
              <Plus className="mr-2 h-4 w-4" />
              New project
            </Button>
          ) : null}
        </div>
      </div>

      {error && (
        <div
          className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200"
          data-testid="aura-error"
        >
          {error}
        </div>
      )}

      {/* Company filter */}
      <section
        className="flex flex-wrap items-end gap-4 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-5"
        data-testid="dashboard-company-filter"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cp-grad text-white shadow-cp-accent">
          <Building2 className="h-5 w-5" />
        </div>
        <div className="min-w-[240px] flex-1 space-y-2">
          <Label htmlFor="dashboard-company-select">Company</Label>
          {loadingCompanies ? (
            <p className="text-sm text-muted-foreground">Loading companies…</p>
          ) : companies.length === 0 ? (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-muted-foreground">No companies yet.</p>
              <Button asChild size="sm">
                <Link to="/transformations/companies">Add companies</Link>
              </Button>
            </div>
          ) : (
            <Select
              value={selectedCompany?.id || companyIdParam || undefined}
              onValueChange={setCompanyId}
            >
              <SelectTrigger
                id="dashboard-company-select"
                className="max-w-md"
                data-testid="dashboard-company-select"
              >
                <SelectValue placeholder="Select a company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name}
                    {company.code ? ` (${company.code})` : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {selectedCompany ? (
          <p className="pb-2 text-sm text-muted-foreground" data-testid="aura-company">
            Showing projects for{' '}
            <span className="font-semibold text-foreground">{selectedCompany.name}</span>
            {selectedCompany.code ? (
              <span className="text-muted-foreground"> · {selectedCompany.code}</span>
            ) : null}
          </p>
        ) : null}
      </section>

      {/* Project cards */}
      <section className="space-y-4" data-testid="aura-projects">
        <div className="flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              Projects
              {selectedCompany ? (
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({projects.length})
                </span>
              ) : null}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Each card is a transformation project under the selected company.
            </p>
          </div>
        </div>

        {!selectedCompany && !loadingCompanies ? (
          <div className="rounded-2xl border border-dashed border-[var(--line)] bg-[var(--surface)] p-10 text-center text-sm text-muted-foreground">
            Select a company to view its projects.
          </div>
        ) : loadingProjects ? (
          <p className="text-sm text-muted-foreground">Loading projects…</p>
        ) : projects.length === 0 ? (
          <div
            className="rounded-2xl border border-dashed border-[var(--line)] bg-[var(--surface)] p-10 text-center"
            data-testid="aura-empty"
          >
            <p className="text-sm text-muted-foreground">
              No projects for {selectedCompany?.name || 'this company'} yet.
            </p>
            {canUpdate ? (
              <Button
                type="button"
                className="mt-4"
                onClick={() => setCreateProjectOpen(true)}
                disabled={!selectedCompany}
              >
                <Plus className="mr-2 h-4 w-4" />
                New project
              </Button>
            ) : null}
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {projects.map((project) => (
              <button
                key={project.id}
                type="button"
                data-testid={`aura-project-${project.id}`}
                onClick={() => navigate(`/transformations/projects/${project.id}`)}
                className="
                  group text-left rounded-2xl border border-[var(--line)] bg-[var(--surface)]
                  p-5 shadow-sm transition-all
                  hover:border-violet-500/40 hover:shadow-md
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/50
                "
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-cp-grad text-white shadow-cp-accent">
                    <ArrowLeftRight className="h-5 w-5" />
                  </div>
                  <Badge
                    variant="outline"
                    className={`rounded-full font-medium ${statusBadgeClass(project.status)}`}
                  >
                    {statusLabel(project.status)}
                  </Badge>
                </div>

                <h3 className="mt-4 text-base font-semibold text-foreground group-hover:text-white">
                  {project.name}
                </h3>
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                  {project.description || 'No description'}
                </p>

                <div className="mt-6 flex items-center justify-between border-t border-[var(--line)] pt-4 text-sm">
                  <span className="text-muted-foreground">Updated</span>
                  <span className="tabular-nums text-foreground">
                    {project.updated_at
                      ? new Date(project.updated_at).toLocaleDateString()
                      : '—'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </section>

      <Dialog open={canUpdate && createProjectOpen} onOpenChange={setCreateProjectOpen}>
        <DialogContent data-testid="aura-create-dialog">
          <form onSubmit={handleCreateProject}>
            <DialogHeader>
              <DialogTitle>New transformation project</DialogTitle>
              <DialogDescription>
                Creates a project under{' '}
                <strong>
                  {selectedCompany?.name || selectedCompany?.code || 'this company'}
                </strong>
                . You can start discovery after it is created.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="aura-project-name">Project name</Label>
                <Input
                  id="aura-project-name"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g. ECC → S/4HANA Wave 1"
                  required
                  data-testid="aura-project-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="aura-project-desc">Description</Label>
                <Textarea
                  id="aura-project-desc"
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                  placeholder="Scope, landscape notes, or goals"
                  rows={3}
                  data-testid="aura-project-desc"
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateProjectOpen(false)}
                disabled={creatingProject}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={creatingProject || !projectName.trim()}>
                {creatingProject ? 'Creating…' : 'Create project'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
